import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Utils from "../../Utils";
import Swal from "sweetalert2";
import _ from "lodash";
import * as Marker from "@markerjs/markerjs3";
import jsPDF from "jspdf";

const RecceTab = ({ mode, psId, canUpload, onUploadRecce }) => {
  /* ✅ Hooks ALWAYS first */
  const fileInputRef = useRef(null);
  const [downloadable, setDownloadable] = useState(false);
  const [recceImages, setRecceImages] = useState([]);
  const [recLoading, setRecLoading] = useState(false);

  const title = mode === "recce" ? "Photoshoot Recce" : "Styling Deck";

  const apiUrl = process.env.REACT_APP_API_URL;

  /* ---------------- FETCH LIST ---------------- */
  const loadRecceImages = () => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/recces/${psId}/${mode}`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          setRecceImages(res.data.data);
          //setDownloadable(res.data.downloadable);
          setDownloadable(res.data.data.some((item) => item.va_feedback)); //== if any image has feedback, allow download
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadRecceImages();
  }, [psId]);

  /* ---------------- FILE UPLOAD ---------------- */

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      Swal.fire({
        icon: "warning",
        title: "Invalid file",
        text: "Only PDF files allowed",
      });
      return;
    }

    Swal.fire({
      title: title,
      text: "Are there any more Recce submission in upcoming days?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      uploadFile(file, result.isConfirmed);
    });
  };

  const uploadFile = (file, moreUploads = false) => {
    setRecLoading(true);

    // ✅ ADD LOADER HERE (BEFORE API CALL)
    Swal.fire({
      title: "Uploading...",
      text: "Please wait",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const formData = new FormData();
    formData.append("pdffile", file);
    formData.append("moreUploads", moreUploads ? 1 : 0);

    axios
      .post(
        Utils.apiUrl(`photoshoot/reccepdf/${psId}/${mode}`),
        formData,
        Utils.apiHeaders(),
      )
      .then((res) => {
        setRecLoading(false);

        Swal.close(); // ✅ CLOSE LOADER FIRST

        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Uploaded!",
            text: res.data.message || "File uploaded successfully",
          });

          let newDets = { status: res.data.newStatus };
          newDets[`more_${mode}`] = moreUploads;

          onUploadRecce(newDets); // ✅ Update parent about new status

          loadRecceImages();
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: res.data.message || "Something went wrong",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        setRecLoading(false);

        Swal.close(); // ✅ CLOSE LOADER ON ERROR ALSO

        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to upload file",
        });
      });
  };

  const submitRecce = () => {
    Swal.fire({
      title: title,
      text: `There are no more ${title} submissions to be made for this photoshoot, are you sure you want to submit to Brand Team?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setRecLoading(true);

      // ✅ ADD LOADER HERE (BEFORE API CALL)
      Swal.fire({
        title: "Submitting...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      axios
        .post(
          Utils.apiUrl(`photoshoot/reccesubmit/${psId}/${mode}`),
          { submit: true },
          Utils.apiHeaders(),
        )
        .then((res) => {
          setRecLoading(false);

          Swal.close(); // ✅ CLOSE LOADER FIRST

          if (res.data.success) {
            Swal.fire({
              icon: "success",
              title: "Submitted!",
              text: res.data.message || "Submission successful",
            });

            let newDets = { status: res.data.newStatus };
            newDets[`more_${mode}`] = false; // ✅ No more uploads allowed after submission

            onUploadRecce(newDets); // ✅ Update parent about new status

            loadRecceImages();
          } else {
            Swal.fire({
              icon: "error",
              title: "Submission Failed",
              text: res.data.message || "Something went wrong",
            });
          }
        })
        .catch((err) => {
          console.log(err);
          setRecLoading(false);

          Swal.close(); // ✅ CLOSE LOADER ON ERROR ALSO

          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to submit to Brand Team",
          });
        });
    });
  };

  /* ---------------- DELETE ---------------- */

  const deleteRecce = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This file will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Optional loader
        Swal.fire({
          title: "Deleting...",
          text: "Please wait",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        axios
          .delete(Utils.apiUrl(`photoshoot/recce/${id}`), Utils.apiHeaders())
          .then((res) => {
            Swal.close(); // close loader

            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: res.data.message || "File deleted successfully",
                timer: 2000,
                showConfirmButton: false,
              });

              loadRecceImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          })
          .catch(() => {
            Swal.close();

            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete file",
            });
          });
      }
    });
  };

  /* ---------------- DRAG & DROP ---------------- */
  const stopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    stopEvent(e);
    const file = e.dataTransfer.files?.[0];
    if (!file || file.type !== "application/pdf") return;

    fileInputRef.current.files = e.dataTransfer.files;
    fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  function preloadRecceImages() {
    const promises = recceImages
      .filter((item) => !_.isNull(item.va_feedback))
      .map((item) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous"; // Add this line
          img.src = `${process.env.REACT_APP_API_URL}/photoshoot/recce/full/${item.fname}`;

          img.onload = () => {
            const annotation = item.va_feedback
              ? JSON.parse(item.va_feedback)
              : null;
            resolve([img, annotation, item.filename]);
          };
          img.onerror = () => {
            console.warn("Failed to load:", img.src);
            resolve(null); // resolve to avoid blocking all
          };
        });
      });
    return Promise.all(promises);
  }

  function raterizeAll(imgs) {
    let markerAreas = [];
    const rasterPromises = imgs.map(([img, annotation, filename], index) => {
      return new Promise((resolve, reject) => {
        markerAreas[index] = new Marker.Renderer();
        markerAreas[index].targetImage = img;

        if (annotation) {
          markerAreas[index]
            .rasterize(annotation)
            .then((dataUrl) => {
              //console.log("Rasterized image ready:", dataUrl);
              resolve([dataUrl, img.width, img.height]);
            })
            .catch((err) => {
              console.log("Failed to rasterize:", err);
              resolve(null); // resolve to avoid blocking all
            });
        } else {
          console.log("No annotation for image:", img.src);
          resolve(filename);
        }
      });
    });
    return Promise.all(rasterPromises);
  }

  const processPDF = () => {
    return preloadRecceImages()
      .then((imgs) => imgs.filter((img) => img !== null))
      .then(raterizeAll)
      .then(generatePDF)
      .catch((err) => {
        console.log("Failed to load or render images:", err);
      });
  };

  const generatePDF = (imgblobs) => {
    const pdf = new jsPDF();
    imgblobs.forEach(([imgData, imgWidth, imgHeight], index) => {
      if (index > 0) pdf.addPage();

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;

      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      imgWidth *= ratio;
      imgHeight *= ratio;

      const x = (pageWidth - imgWidth) / 2;
      const y = margin; // top aligned

      pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight); // A4 size with some margin
    });
    pdf.save(`photoshoot_${psId}_${mode}.pdf`);
  };

  const downloadPdf = () => {
    Swal.fire({
      title: "Download PDF?",
      text: `Photoshoot_${psId}_${mode}.pdf will be downloaded`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, download",
    }).then((result) => {
      if (result.isConfirmed) {
        // 🔄 Show loader
        Swal.fire({
          title: "Downloading...",
          text: "Please wait while your PDF is being prepared",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        processPDF()
          .then(() => {
            // ✅ Show success alert after download
            Swal.fire({
              icon: "success",
              title: "Downloaded!",
              text: "PDF downloaded successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((err) => {
            console.error(err);

            // ERROR ALERT
            Swal.fire({
              icon: "error",
              title: "Download Failed",
              text: "Unable to download PDF. Please try again.",
            });
          });
      }
    });
  };

  const deleteAllRecces = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "All submitted recces will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete all!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(
            Utils.apiUrl(`photoshoot/recce/all/${psId}`),
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: res.data.message || "All recces deleted successfully",
                timer: 2000,
                showConfirmButton: false,
              });
              loadRecceImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          });
      }
    });
  };

  /* ---------------- UI ---------------- */
  return (
    <div id={`tab-${mode}`} className={`tab-panel active`}>
      {mode === "recce" && (
        <div className="instruction-note mt-4 mb-4">
          <i className="fas fa-lightbulb mr-2"></i>
          Required only for New Build and Conversion Hotel.
        </div>
      )}

      {recLoading && (
        <div className="text-blue-500 font-semibold py-4">Uploading…</div>
      )}

      {!recLoading && (
        <>
          {canUpload() === 1 && (
            <div
              className="drop-zone cursor-pointer"
              onClick={() => fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={stopEvent}
              onDragLeave={stopEvent}
            >
              <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
              <p>Drag & Drop PDF or click to upload</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}
          {downloadable && (
            <div className="instruction-note mt-4 mb-4">
              <i className="fas fa-lightbulb mr-2"></i>
              You can download Brand Team feedback as{" "}
              <button
                className="btn rounded-md bg-yellow-100 p-1 pr-2 text-sm"
                onClick={downloadPdf}
              >
                <i className="fas fa-download mx-1"></i>
                PDF
              </button>
            </div>
          )}
          {canUpload() === 1 && (
            <div className="flex justify-end">
              {recceImages.filter((item) => item.status == "submitted").length >
                0 && (
                <button
                  className="rounded-md p-1 pr-2 bg-red-700 text-white mt-4"
                  onClick={deleteAllRecces}
                >
                  Delete All Submissions
                </button>
              )}
              {recceImages.filter((item) => item.status !== "submitted")
                .length > 0 && (
                <button
                  className="rounded-md p-1 pr-2 bg-blue-600 text-white mx-3 mt-4"
                  onClick={submitRecce}
                >
                  Finalize all recce submission
                </button>
              )}
            </div>
          )}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {recceImages
              .filter(
                (item) =>
                  (canUpload() === 1 && item.status == "submitted") ||
                  item.va_feedback,
              )
              .map((item, index) => (
                <div
                  key={item.id}
                  className="relative bg-white rounded-lg shadow border"
                >
                  <div className="h-40 bg-gray-100 flex items-center justify-center">
                    <img
                      src={`${apiUrl}/photoshoot/recce/thumb/${item.fname}`}
                      className="object-cover w-full h-full"
                      alt=""
                    />
                  </div>

                  <div className="p-3 text-sm font-semibold truncate">
                    File {index + 1}
                    {item.ai_feedback && (
                      <div className="text-xs font-normal">
                        {item.ai_feedback}
                      </div>
                    )}
                  </div>

                  {canUpload() === 1 && item.status === "submitted" && (
                    <button
                      className="absolute top-2 right-2 text-red-700 bg-red-100 p-1 rounded text-sm"
                      onClick={() => deleteRecce(item.id)}
                    >
                      <i className="fa-solid fa-x"></i>
                    </button>
                  )}

                  {item.va_feedback && (
                    <a
                      className="absolute top-2 right-2 bg-green-700 text-white p-1 rounded text-sm"
                      href={`${process.env.PUBLIC_URL}/photoshoot-recce/${psId}/${mode}/${item.fname}`}
                    >
                      <i className="fa-solid fa-magnifying-glass"></i>
                    </a>
                  )}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RecceTab;
